import React, { useState, useEffect } from "react";
import {
    Table, TableBody, TableHead, TablePagination, Chip, Box, Typography,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Toolbar, InputAdornment, FormControl, InputLabel, Select, MenuItem, Divider
} from "@mui/material";
import {
    Bolt, AccessTime, Edit, Visibility, CheckCircle, Block, Search, FilterList, Map, Settings
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { StyledTableContainer, StyledTableCell, StyledTableRow, StyledPaper } from "../assets/styles/tableStyle";
import { Zone, updateZone } from "../services/zoneService";
import FancyContextMenu, { ContextMenuAction } from "./FancyContextMenu";

interface Props {
    zones: Zone[];
    onDataChange?: () => void;
    onEdit?: (zone: Zone) => void;
}

const ZoneTable: React.FC<Props> = ({ zones, onDataChange, onEdit }) => {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Local state for Optimistic Updates (Syncs with props)
    const [tableZones, setTableZones] = useState<Zone[]>(zones);

    // Filter State
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [filteredZones, setFilteredZones] = useState<Zone[]>(zones);

    // Keep local state in sync when parent sends new data
    useEffect(() => {
        setTableZones(zones);
    }, [zones]);

    // Filtering Logic
    useEffect(() => {
        let result = tableZones;

        // 1. Search Filter
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            result = result.filter(z =>
                z.name.toLowerCase().includes(lowerTerm) ||
                String(z.id).includes(lowerTerm)
            );
        }

        // 2. Status Filter
        if (statusFilter !== "all") {
            const isActive = statusFilter === "active";
            result = result.filter(z => z.isActive === isActive);
        }

        setFilteredZones(result);
        setPage(0); // Reset page on filter change
    }, [tableZones, searchTerm, statusFilter]);

    // Context Menu State
    const [menu, setMenu] = useState<{ top: number; left: number } | null>(null);
    const [selectedRow, setSelectedRow] = useState<Zone | null>(null);

    // Quick Edit State
    const [editOpen, setEditOpen] = useState(false);
    const [editData, setEditData] = useState<Partial<Zone>>({});

    // -- Handlers --

    const handleContextMenu = (event: React.MouseEvent, row: Zone) => {
        event.preventDefault();
        event.stopPropagation();
        setSelectedRow(row);
        setMenu({ top: event.clientY, left: event.clientX });
    };

    const handleCloseMenu = () => setMenu(null);

    const handleRowClick = (zone: Zone) => {
        navigate(`/zones/${zone.id}`);
    };

    const handleToggleActive = async (zone: Zone) => {
        handleCloseMenu(); // Close menu first to prevent focus trap
        // Optimistic Update: Update UI immediately
        const newStatus = !zone.isActive;
        setTableZones(prev => prev.map(z => z.id === zone.id ? { ...z, isActive: newStatus } : z));

        try {
            await updateZone(zone.id, { isActive: newStatus });
            if (onDataChange) onDataChange(); // Tell parent to refresh in background
        } catch (error) {
            console.error("Failed to toggle status", error);
            // Revert on failure
            setTableZones(prev => prev.map(z => z.id === zone.id ? { ...z, isActive: !newStatus } : z));
        }
    };

    const handleQuickEdit = (zone: Zone) => {
        handleCloseMenu();
        // Slight timeout to let menu close animation finish (fixes aria-hidden warning)
        setTimeout(() => {
            setEditData({
                surgeMultiplier: zone.surgeMultiplier,
                rushHourMultiplier: zone.rushHourMultiplier || 1.2,
                rushHourStart: zone.rushHourStart,
                rushHourEnd: zone.rushHourEnd,
                geocodeProvider: zone.geocodeProvider,
                placesProvider: zone.placesProvider,
                routeProvider: zone.routeProvider
            });
            setSelectedRow(zone);
            setEditOpen(true);
        }, 50);
    };

    const handleFullEdit = (zone: Zone) => {
        handleCloseMenu();
        if (onEdit) {
            setTimeout(() => onEdit(zone), 50);
        }
    };

    // Helper: Enforce 0-24 Logic
    const handleHourChange = (field: 'rushHourStart' | 'rushHourEnd', valStr: string) => {
        let val = parseInt(valStr);
        if (isNaN(val)) val = 0;
        // Strict Clamping
        if (val < 0) val = 0;
        if (val > 24) val = 24;

        setEditData(prev => ({ ...prev, [field]: val }));
    };

    const handleSaveQuickEdit = async () => {
        if (!selectedRow) return;

        // Validation: Start cannot be after End
        if ((editData.rushHourStart ?? 0) > (editData.rushHourEnd ?? 0)) {
            alert("Rush Hour Start cannot be later than End time.");
            return;
        }

        // Optimistic Update: Update UI immediately
        setTableZones(prev => prev.map(z =>
            z.id === selectedRow.id ? { ...z, ...editData } : z
        ));
        setEditOpen(false);

        try {
            await updateZone(selectedRow.id, editData);
            if (onDataChange) onDataChange(); // Tell parent to refresh in background
        } catch (error) {
            console.error("Update failed", error);
            // Ideally revert here if needed, or rely on parent fetch to fix it
        }
    };

    // -- Context Menu Config --
    const actions: ContextMenuAction[] = [
        {
            label: "Manage Pricing & Plans",
            icon: <Visibility color="primary" fontSize="small" />,
            onClick: () => selectedRow && navigate(`/zones/${selectedRow.id}`),
            color: "primary",
        },
        {
            label: "Edit Zone Settings",
            icon: <Settings color="warning" fontSize="small" />,
            onClick: () => selectedRow && handleFullEdit(selectedRow),
            color: "warning",
        },
        {
            label: "Quick Config",
            icon: <Edit color="info" fontSize="small" />,
            onClick: () => selectedRow && handleQuickEdit(selectedRow),
            color: "info",
        },
        {
            dividerBefore: true,
            label: selectedRow?.isActive ? "Deactivate Zone" : "Activate Zone",
            icon: selectedRow?.isActive ? <Block color="error" fontSize="small" /> : <CheckCircle color="success" fontSize="small" />,
            onClick: () => selectedRow && handleToggleActive(selectedRow),
            color: selectedRow?.isActive ? "error" : "success",
        }
    ];

    const paginatedZones = filteredZones.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <StyledPaper sx={{ maxWidth: "100%", width: "100%", margin: '0 auto', boxShadow: 'none' }}>
            <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, px: 2, py: 1 }}>
                <Typography variant="h6">Zone Management</Typography>

                {/* Controls Container */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Search Input */}
                    <TextField
                        placeholder="Search Name, ID..."
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ minWidth: 220 }}
                    />

                    {/* Status Filter */}
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status"
                            onChange={(e) => setStatusFilter(e.target.value)}
                            startAdornment={<FilterList fontSize="small" sx={{ mr: 1, color: 'action.active' }} />}
                        >
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Clear Button */}
                    {(searchTerm || statusFilter !== 'all') && (
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => {
                                setSearchTerm("");
                                setStatusFilter("all");
                            }}
                        >
                            Clear
                        </Button>
                    )}
                </Box>
            </Toolbar>

            <StyledTableContainer sx={{ boxShadow: 'none', borderTop: '1px solid #e0e0e0' }}>
                <Table stickyHeader>
                    <TableHead>
                        <StyledTableRow>
                            <StyledTableCell>ID</StyledTableCell>
                            <StyledTableCell>Name</StyledTableCell>
                            <StyledTableCell>Status</StyledTableCell>
                            <StyledTableCell>Map Config</StyledTableCell>
                            <StyledTableCell>Base Surge</StyledTableCell>
                            <StyledTableCell>Rush Hour</StyledTableCell>
                            <StyledTableCell>Overrides</StyledTableCell>
                        </StyledTableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedZones.map((zone) => (
                            <StyledTableRow
                                key={zone.id}
                                hover
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleRowClick(zone)}
                                onContextMenu={(e) => handleContextMenu(e, zone)}
                            >
                                <StyledTableCell>#{zone.id}</StyledTableCell>
                                <StyledTableCell>
                                    <Typography variant="body2" fontWeight="bold">{zone.name}</Typography>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Chip
                                        label={zone.isActive ? "Active" : "Inactive"}
                                        color={zone.isActive ? "success" : "error"}
                                        size="small"
                                        variant="filled"
                                    />
                                </StyledTableCell>
                                {/* 4. MODIFIED: Visual Stack for All 3 Providers */}
                                <StyledTableCell>
                                    {(!zone.geocodeProvider && !zone.placesProvider && !zone.routeProvider) ? (
                                        <Chip label="Global" size="small" variant="outlined" color="default" />
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', width: 20 }}>G:</Typography>
                                                <Typography variant="caption" fontWeight="bold" color={zone.geocodeProvider === 'osm' ? 'info.main' : 'text.primary'}>
                                                    {zone.geocodeProvider ? zone.geocodeProvider.toUpperCase() : "GLB"}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', width: 20 }}>P:</Typography>
                                                <Typography variant="caption" fontWeight="bold" color={zone.placesProvider === 'osm' ? 'info.main' : 'text.primary'}>
                                                    {zone.placesProvider ? zone.placesProvider.toUpperCase() : "GLB"}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', width: 20 }}>R:</Typography>
                                                <Typography variant="caption" fontWeight="bold" color={zone.routeProvider === 'osm' ? 'info.main' : 'text.primary'}>
                                                    {zone.routeProvider ? zone.routeProvider.toUpperCase() : "GLB"}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Chip
                                        icon={<Bolt fontSize="small" />}
                                        label={`${zone.surgeMultiplier}x`}
                                        color={zone.surgeMultiplier > 1 ? "warning" : "default"}
                                        size="small"
                                    />
                                </StyledTableCell>
                                <StyledTableCell>
                                    {(zone.rushHourStart !== null && zone.rushHourEnd !== null) ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AccessTime fontSize="small" color="action" />
                                            <Typography variant="caption">
                                                {zone.rushHourStart}:00 - {zone.rushHourEnd}:00
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" color="textSecondary">-</Typography>
                                    )}
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                        {/* Ride Rates Override */}
                                        {zone._count?.zoneRates ? (
                                            <Chip label={`${zone._count.zoneRates} Rate Rules`} size="small" color="primary" variant="outlined" />
                                        ) : null}

                                        {/* Subscription Plans Override  */}
                                        {(zone as any)._count?.subscriptionOverrides ? (
                                            <Chip label={`${(zone as any)._count.subscriptionOverrides} Plan Rules`} size="small" color="secondary" variant="outlined" />
                                        ) : null}

                                        {/* Fallback */}
                                        {!zone._count?.zoneRates && !(zone as any)._count?.subscriptionOverrides && (
                                            <Typography variant="caption" color="textSecondary">Global</Typography>
                                        )}
                                    </Box>
                                </StyledTableCell>
                            </StyledTableRow>
                        ))}
                        {paginatedZones.length === 0 && (
                            <StyledTableRow>
                                <StyledTableCell colSpan={7} align="center">
                                    <Typography color="textSecondary" sx={{ py: 3 }}>No zones found matching filters.</Typography>
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </StyledTableContainer>

            <TablePagination
                component="div"
                count={filteredZones.length}
                page={page}
                onPageChange={(e, n) => setPage(n)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value))}
                rowsPerPageOptions={[10, 25, 50]}
            />

            {/* Context Menu */}
            <FancyContextMenu
                actions={actions}
                open={Boolean(menu)}
                onClose={handleCloseMenu}
                anchorPosition={menu}
                title={selectedRow?.name || "Zone Actions"}
            />

            {/* Quick Edit Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
                <DialogTitle>Quick Config: {selectedRow?.name}</DialogTitle>
                <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 350 }}>
                    {/* 5. ADD: Map Provider Selectors (All 3) */}
                    <Box sx={{ mb: 1 }}>
                        <Divider sx={{ mb: 2 }}>Map Providers</Divider>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                            <FormControl size="small">
                                <InputLabel>Geocode</InputLabel>
                                <Select
                                    value={editData.geocodeProvider || ""}
                                    label="Geocode"
                                    onChange={(e) => setEditData({ ...editData, geocodeProvider: e.target.value || null })}
                                >
                                    <MenuItem value=""><em>Global</em></MenuItem>
                                    <MenuItem value="google">Google</MenuItem>
                                    <MenuItem value="osm">ZapppIt-OSM</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small">
                                <InputLabel>Places</InputLabel>
                                <Select
                                    value={editData.placesProvider || ""}
                                    label="Places"
                                    onChange={(e) => setEditData({ ...editData, placesProvider: e.target.value || null })}
                                >
                                    <MenuItem value=""><em>Global</em></MenuItem>
                                    <MenuItem value="google">Google</MenuItem>
                                    <MenuItem value="osm">ZapppIt-OSM</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small">
                                <InputLabel>Routes</InputLabel>
                                <Select
                                    value={editData.routeProvider || ""}
                                    label="Routes"
                                    onChange={(e) => setEditData({ ...editData, routeProvider: e.target.value || null })}
                                >
                                    <MenuItem value=""><em>Global</em></MenuItem>
                                    <MenuItem value="google">Google</MenuItem>
                                    <MenuItem value="osm">ZapppIt-OSM</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Divider>Pricing & Surge</Divider>

                    <TextField
                        label="Base Surge Multiplier"
                        type="number"
                        inputProps={{ step: 0.1, min: 1.0 }}
                        value={editData.surgeMultiplier}
                        onChange={(e) => setEditData({ ...editData, surgeMultiplier: parseFloat(e.target.value) })}
                        fullWidth
                        margin="dense"
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                            label="Rush Hour Start (0-24)"
                            type="number"
                            inputProps={{ min: 0, max: 24 }}
                            value={editData.rushHourStart ?? ""}
                            onChange={(e) => handleHourChange('rushHourStart', e.target.value)}
                        />
                        <TextField
                            label="Rush Hour End (0-24)"
                            type="number"
                            inputProps={{ min: 0, max: 24 }}
                            value={editData.rushHourEnd ?? ""}
                            onChange={(e) => handleHourChange('rushHourEnd', e.target.value)}
                        />
                    </Box>
                    <TextField
                        label="Rush Hour Multiplier"
                        type="number"
                        inputProps={{ step: 0.1, min: 1.0 }}
                        value={editData.rushHourMultiplier}
                        onChange={(e) => setEditData({ ...editData, rushHourMultiplier: parseFloat(e.target.value) })}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveQuickEdit}>Save Changes</Button>
                </DialogActions>
            </Dialog>
        </StyledPaper>
    );
};

export default ZoneTable;